import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dashboardsApi, radarApi, tagsApi } from '../../services/api';
import TreeChart from '../TrendRadarChart/components/TreeChart';
import ZoomControls from '../TrendRadarChart/components/ZoomControls';
import SearchBar from '../TrendRadarChart/components/SearchBar';
import Breadcrumb from '../TrendRadarChart/components/Breadcrumb';
import useSearch from '../TrendRadarChart/hooks/useSearch';
import Tooltip from '../TrendRadarChart/components/Tooltip';
import { APPROVAL_STAGE_COLORS } from '../../constants/radar';
import LoadingSpinner from '../common/LoadingSpinner';
import NavigationPanel from '../TrendRadarChart/components/NavigationPanel';
import {
  Container,
  Header,
  HeaderLeft,
  HeaderRight,
  BackButton,
  Title,
  HeaderButton,
  Content,
  ChartPanel,
  TabContainer,
  TabButton,
} from './styles';
import { transformApiDataToRadarFormat } from '../../utils/dataTransformers';
import useChartData from '../TrendRadarChart/hooks/useChartData';
import useZoomPan from '../TrendRadarChart/hooks/useZoomPan';
import useTooltip from '../TrendRadarChart/hooks/useTooltip';
import { FaEyeSlash, FaEye, FaArrowLeft, FaGlobe, FaProjectDiagram } from 'react-icons/fa';
import { useBreadcrumb } from '../TrendRadarChart/hooks/useBreadcrumb';
import { TREE_CHART_MODES } from '../../constants/radar';
import { useAlert } from '../../contexts/AlertContext';
import RadarChart from '../TrendRadarChart/components/RadarChart';
import { COLORS, SEGMENT_TYPE } from '../../constants/radar';
import QuadrantRadarChart from '../TrendRadarChart/components/QuadrantRadarChart';
import TagSelectorModalComponent from '../common/TagSelectorModal';


const DashboardConfiguration = () => {
  const navigate = useNavigate();
  // 1. All state declarations first
  const [activeTab, setActiveTab] = useState('configure');
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardHierarchyData, setDashboardHierarchyData] = useState(null);
  const [isSelectionPanelCollapsed, setIsSelectionPanelCollapsed] = useState(false);
  const [isNavigationPanelCollapsed, setIsNavigationPanelCollapsed] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [initialCheckedLeafs, setInitialCheckedLeafs] = useState(new Set());
  const [treeExpandedState, setTreeExpandedState] = useState([]);
  const [arePanelsVisible, setArePanelsVisible] = useState(true);
  const [isTreeExpanded, setIsTreeExpanded] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [viewMode, setViewMode] = useState('traditional');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFullRadar, setShowFullRadar] = useState(false);
  const [fullHierarchyData, setFullHierarchyData] = useState(null);

  const { dashboardId } = useParams();

  // 2. Context hooks
  const { showAlert, startLoading, stopLoading } = useAlert();

  // Helper function to count nodes in tree
  const countNodes = (node) => {
    if (!node) return 0;
    const subcategoriesCount = (node.subcategories || [])
      .reduce((sum, child) => sum + countNodes(child), 0);
    return 1 + subcategoriesCount;
  };

  // Initialize data for Tree view
  const {
    selectedSubcategory: treeSubcategory,
    handleSelection: handleTreeSelection,
    handleBack: handleTreeBack,
  } = useChartData(selectedProject?.data || {}, 'tree');

  const fetchDashboard = useCallback(async () => {
    try {
      if (!dashboardId) {
        setSelectedDashboard(null);
        return;
      }
  
      setIsDashboardLoading(true);
  
      // Get the dashboard data
      const dashboardData = await dashboardsApi.getDashboardById(dashboardId);

      setSelectedDashboard({
        ...dashboardData,
        data: {}
      });

      const hierarchyData = await radarApi.getProjectHierarchy(dashboardData.project.id);

      // Transform hierarchy data and update state
      const transformedHierarchy = transformApiDataToRadarFormat(hierarchyData);
      
      setSelectedProject({
        ...dashboardData.project,
        data: transformedHierarchy
      });

      // Reset any existing selections
      handleTreeSelection(null);
  
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setError('Failed to load dashboard data');
    } finally {
      setIsDashboardLoading(false);
    }
  }, [
    dashboardId,
    handleTreeSelection,
    setError,
    setIsDashboardLoading,
    setSelectedDashboard,
    setSelectedProject
  ]);

  useEffect(() => {
    if (dashboardId) {
      fetchDashboard();
    }
  }, [dashboardId, fetchDashboard]);

  // Add filterDataByTags function before getFilteredData
  const filterDataByTags = useCallback((data, selectedTags) => {
    if (!selectedTags.length) return data;

    const filterNodes = (node) => {
      if (!node) return null;

      // Check if it's a leaf node by segmentType instead of subcategories
      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        const hasSelectedTag = selectedTags.some(selectedTag => 
          node.tags.some(nodeTag => 
            nodeTag.id === selectedTag.id || 
            nodeTag.parentTagId === selectedTag.id
          )
        );

        return hasSelectedTag ? node : null;
      }

      // Filter subcategories
      const filteredSubcategories = (node.subcategories || [])
        .map(child => filterNodes(child))
        .filter(Boolean);

      // Only include non-leaf nodes if they have filtered subcategories
      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    // Return only filtered results, not the original data if no matches
    return filterNodes(data);
  }, []);

  // Define getFilteredData after filterDataByTags
  const getFilteredData = useCallback(() => {
    let filteredData = showFullRadar ? fullHierarchyData : selectedProject?.data;
    if (!filteredData) return null;

    // First apply selection filter if needed
    if (showOnlySelected && activeTab === 'configure' && selectedDashboard) {
      const filterBySelection = (node) => {
        if (!node) return null;

        if (!node.subcategories || node.subcategories.length === 0) {
          return initialCheckedLeafs.has(node.leafSegmentId) ? node : null;
        }

        const filteredSubcategories = (node.subcategories || [])
          .map(filterBySelection)
          .filter(Boolean);

        return filteredSubcategories.length > 0
          ? { ...node, subcategories: filteredSubcategories }
          : null;
      };

      const selectionFiltered = filterBySelection(filteredData);
      // If no nodes match selection criteria, return empty root node
      filteredData = selectionFiltered || {
        ...filteredData,
        subcategories: []
      };
    }

    // Then apply tag filter
    const tagFiltered = filterDataByTags(filteredData, selectedTags);
    
    // If no nodes match tag criteria, return empty root node
    return tagFiltered || {
      ...filteredData,
      subcategories: []
    };
  }, [
    selectedProject?.data,
    showOnlySelected,
    activeTab,
    selectedDashboard?.id,
    initialCheckedLeafs,
    selectedTags,
    filterDataByTags,
    showFullRadar,
    fullHierarchyData
  ]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  const {
    searchTerm: treeSearchTerm,
    searchResults: treeSearchResults,
    showResults: showTreeResults,
    isGlobalSearch: isTreeGlobalSearch,
    handleSearch: handleTreeSearch,
    handleResultSelect: handleTreeResultSelect,
    setIsGlobalSearch: setIsTreeGlobalSearch
  } = useSearch(filteredData || {}, treeSubcategory);

  // 4. Initialize data for Radar view
  const {
    selectedSubcategory: radarSubcategory,
    handleSelection: handleRadarSelection,
    handleBack: handleRadarBack,
  } = useChartData(dashboardHierarchyData || {}, 'radar');

  const {
    searchTerm: radarSearchTerm,
    searchResults: radarSearchResults,
    showResults: showRadarResults,
    isGlobalSearch: isRadarGlobalSearch,
    handleSearch: handleRadarSearch,
    handleResultSelect: handleRadarResultSelect,
    setIsGlobalSearch: setIsRadarGlobalSearch
  } = useSearch(dashboardHierarchyData || {}, radarSubcategory);

  const {
    tooltipData,
    tooltipPosition,
    hoveredItem,
    handleHover,
    handleMouseOut
  } = useTooltip();

  const {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleResetPosition,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  } = useZoomPan();

  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);

  // Get the breadcrumb path based on active tab
  const breadcrumbPath = useBreadcrumb(
    activeTab === 'configure' 
      ? selectedProject?.data || {} 
      : dashboardHierarchyData || {},
    activeTab === 'configure' ? treeSubcategory : radarSubcategory
  );

  // First define isLeafAssigned
  const isLeafAssigned = useCallback((leafId) => {
    if (!selectedDashboard?.dashboardLeafSegments) {
      return false;
    }
    return selectedDashboard.dashboardLeafSegments.some(
      (leafSegment) => leafSegment.leafSegment?.segmentId === leafId
    );
  }, [selectedDashboard]);

  useEffect(() => {
    if (dashboardId) {

      const fetchDashboard = async () => {
        try {
          if (!dashboardId) {
            setSelectedDashboard(null);
            return;
          }
      
          setIsDashboardLoading(true);
      
          // Get the dashboard data
          const dashboardData = await dashboardsApi.getDashboardById(dashboardId);

          setSelectedDashboard({
            ...dashboardData,
            //data: transformedData
            data: {}
          });

          const hierarchyData = await radarApi.getProjectHierarchy(dashboardData.project.id);

          // Transform hierarchy data and update state
          const transformedHierarchy = transformApiDataToRadarFormat(hierarchyData);
          
          setSelectedProject({
            ...dashboardData.project,
            data: transformedHierarchy
          });

          // Reset any existing selections
          handleTreeSelection(null);
      
        } catch (error) {
          console.error('Error loading dashboard:', error);
          setError('Failed to load dashboard data');
        } finally {
          setIsDashboardLoading(false);
        }
      };
      fetchDashboard();
    }
  }, [dashboardId]);

  // Add effect to initialize checked leafs based on dashboard data
  useEffect(() => {
    if (!selectedDashboard) {
      setInitialCheckedLeafs(new Set());
      return;
    }

    if (selectedDashboard.dashboardLeafSegments) {
      const checkedIds = new Set(
        selectedDashboard.dashboardLeafSegments
          .filter(segment => segment.leafSegment != null)
          .map(segment => segment.leafSegment.id)
      );
      setInitialCheckedLeafs(checkedIds);
    } else {
      setInitialCheckedLeafs(new Set());
    }
  }, [selectedDashboard]);

  useEffect(() => {
    const fetchTags = async () => {
      setIsTagsLoading(true);
      try {
        const tagsData = await tagsApi.getTagHierarchy();
        setTags(tagsData);
      } catch (error) {
        console.error('Failed to load tags:', error);
      } finally {
        setIsTagsLoading(false);
      }
    };
    fetchTags();
  }, []);

  // Fetch full hierarchy when toggle is enabled
  useEffect(() => {
    const fetchFullHierarchy = async () => {
      if (showFullRadar && !fullHierarchyData) {
        try {
          const hierarchyData = await radarApi.getHierarchy();
          const transformed = transformApiDataToRadarFormat(hierarchyData);
          setFullHierarchyData(transformed);
        } catch (error) {
          console.error('Error loading full hierarchy:', error);
        }
      }
    };
    fetchFullHierarchy();
  }, [showFullRadar, fullHierarchyData]);

  // Add this function before the useEffect for preview tab
  const transformDashboardData = useCallback((data) => {

    if (!data || !selectedDashboard?.dashboardInternalSegments) {
      return data;
    }

    const isNodeUnlinked = (nodeId) => {
      const isUnlinked = selectedDashboard.dashboardInternalSegments.some(
        internalSegment => internalSegment.internalSegment.segment.id === nodeId
      );
      return isUnlinked;
    };

    const transformNode = (node, depth = 0) => {
      
      if (!node) {
        return null;
      }

      // If it's a leaf node, return as is
      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        return node;
      }

      // Transform all subcategories first, incrementing depth
      const transformedSubcategories = (node.subcategories || [])
        .map(subNode => transformNode(subNode, depth + 1))
        .filter(Boolean)
        .reduce((acc, curr) => {
          // If curr is an array (result of an unlinked node), spread its items
          // Otherwise, add the item itself
          return Array.isArray(curr) ? [...acc, ...curr] : [...acc, curr];
        }, []);

      // If this is an internal node and it's unlinked
      if (node.segmentType === 'Internal' && depth > 0 && isNodeUnlinked(node.id)) {
        return transformedSubcategories;
      }

      // Otherwise, return the node with transformed subcategories
      return {
        ...node,
        subcategories: transformedSubcategories
      };
    };

    // Start transformation from root
    const transformedData = transformNode(data);
    const result = Array.isArray(transformedData) ? transformedData[0] : transformedData;
    return result;
  }, [selectedDashboard?.dashboardInternalSegments]);

  // Update the preview tab useEffect
  useEffect(() => {
    const fetchDashboardHierarchy = async () => {
      if (activeTab === 'preview' && selectedDashboard) {
        try {
          setIsDashboardLoading(true);
          const hierarchyData = await radarApi.getDashboardHierarchy(selectedDashboard.id);
          const transformedHierarchy = transformApiDataToRadarFormat(hierarchyData);
          // Apply the unlinked nodes transformation
          const filteredHierarchy = transformDashboardData(transformedHierarchy);
          setDashboardHierarchyData(filteredHierarchy);
        } catch (error) {
          console.error('Error loading dashboard hierarchy:', error);
          showAlert('Failed to load dashboard preview', 'error', error);
        } finally {
          setIsDashboardLoading(false);
        }
      }
    };

    fetchDashboardHierarchy();
  }, [activeTab, selectedDashboard, showAlert, transformDashboardData]);

  const findNodeById = (tree, id) => {
    if (tree.id === id) return tree;
    if (tree.subcategories) {
      for (const sub of tree.subcategories) {
        const found = findNodeById(sub, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleReset = () => {
    handleTreeSelection(null);
    //setExpandedPath([]);
  };

  const handleLeafToggle = async (leafId, isChecked) => {
    try {
      if (isChecked) {
        const response = await dashboardsApi.addLeafToDashboard(dashboardId, leafId);
        // Update local state using the response data structure
        setSelectedDashboard(prevDashboard => {
          // Find the new segment by comparing with previous state
          const newSegment = response.dashboardLeafSegments.find(newSeg =>
            !prevDashboard.dashboardLeafSegments?.some(
              oldSeg => oldSeg.leafSegment?.id === newSeg.leafSegment?.id
            )
          );
          
          if (!newSegment) {
            console.error('Could not find newly added segment');
            return prevDashboard;
          }

          return {
            ...prevDashboard,
            dashboardLeafSegments: [
              ...(prevDashboard.dashboardLeafSegments || []),
              newSegment
            ]
          };
        });
        // Update checked state with the segmentId
        setInitialCheckedLeafs(prev => new Set([...prev, leafId]));
        showAlert('Leaf added to dashboard successfully', 'success');
      } else {
        await dashboardsApi.removeLeafFromDashboard(dashboardId, leafId);
        // Update local state
        setSelectedDashboard(prevDashboard => ({
          ...prevDashboard,
          dashboardLeafSegments: prevDashboard.dashboardLeafSegments.filter(
            segment => segment.leafSegment?.id !== leafId
          )
        }));
        // Update checked state
        setInitialCheckedLeafs(prev => {
          const next = new Set(prev);
          next.delete(leafId);
          return next;
        });
        showAlert('Leaf removed from dashboard successfully', 'success');
      }
    } catch (error) {
      showAlert(error.message, 'error', error);
      throw error;
    }
  };

  const handleAddInternalNode = async (node) => {
    try {
      await dashboardsApi.addInternalNodeToDashboard(dashboardId, node.internalSegmentId);
      // Update local state instead of fetching
      setSelectedDashboard(prevDashboard => ({
        ...prevDashboard,
        dashboardInternalSegments: [
          ...(prevDashboard.dashboardInternalSegments || []),
          {
            internalSegment: {
              segment: {
                id: node.id,
                // Add other necessary segment properties
                name: node.name,
                segmentType: node.segmentType
              }
            }
          }
        ]
      }));
      showAlert('Internal node unlinked from dashboard', 'success');
    } catch (error) {
      showAlert(error.message, 'error', error);
    }
  };

  const handleRemoveInternalNode = async (node) => {
    try {
      await dashboardsApi.removeInternalNodeFromDashboard(dashboardId, node.internalSegmentId);
      // Update local state instead of fetching
      setSelectedDashboard(prevDashboard => ({
        ...prevDashboard,
        dashboardInternalSegments: prevDashboard.dashboardInternalSegments.filter(
          internalSegment => internalSegment.internalSegment.segment.id !== node.id
        )
      }));
      showAlert('Internal node linked back to dashboard', 'success');
    } catch (error) {
      showAlert(error.message, 'error', error);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      }
      return [...prev, tag];
    });
  };

  // Memoize handleChartSelection to prevent unnecessary re-renders
  const handleChartSelection = useCallback((item) => {
    
    if (activeTab === 'configure') {
      handleTreeSelection(item);
    } else if (activeTab === 'preview') {
      handleRadarSelection(item);
    }
  }, [activeTab, handleTreeSelection, handleRadarSelection]);

  const handleChartBack = () => {
    if (activeTab === 'configure') {
      handleTreeBack();
    } else {
      handleRadarBack();
    }
  };

  // Memoize search handlers
  const handleSearch = useCallback((term) => {

    if (activeTab === 'configure') {
      handleTreeSearch(term);
    } else if (activeTab === 'preview') {
      handleRadarSearch(term);
    }
  }, [activeTab, handleTreeSearch, handleRadarSearch]);

  const handleResultSelect = useCallback((result) => {

    if (activeTab === 'configure') {
      return handleTreeResultSelect(result);
    } else if (activeTab === 'preview') {
      return handleRadarResultSelect(result);
    }
  }, [activeTab, handleTreeResultSelect, handleRadarResultSelect]);

  // Get the current states based on active tab
  const currentSelectedSubcategory = activeTab === 'configure' 
    ? treeSubcategory 
    : radarSubcategory;

  const currentSearchTerm = activeTab === 'configure' 
    ? treeSearchTerm 
    : radarSearchTerm;

  const currentSearchResults = activeTab === 'configure' 
    ? treeSearchResults 
    : radarSearchResults;

  const currentShowResults = activeTab === 'configure' 
    ? showTreeResults 
    : showRadarResults;

  const currentIsGlobalSearch = activeTab === 'configure'
    ? isTreeGlobalSearch
    : isRadarGlobalSearch;

  const selectedCount = selectedDashboard?.dashboardLeafSegments?.length || 0;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Project
          </BackButton>
          <Title>Dashboard Configuration{` | ${selectedProject?.name} | ${selectedDashboard?.name}`}</Title>
        </HeaderLeft>
        {!isDashboardLoading && selectedDashboard && (
          <HeaderRight>
            <HeaderButton onClick={() => setShowFullRadar(!showFullRadar)}>
              {showFullRadar ? (
                <>
                  <FaGlobe /> Showing: Full Radar
                </>
              ) : (
                <>
                  <FaProjectDiagram /> Showing: Project Technologies
                </>
              )}
            </HeaderButton>
            <HeaderButton onClick={() => setArePanelsVisible(!arePanelsVisible)}>
              {arePanelsVisible ? (
                <>
                  <FaEyeSlash /> Hide Panels
                </>
              ) : (
                <>
                  <FaEye /> Show Panels
                </>
              )}
            </HeaderButton>
          </HeaderRight>
        )}
      </Header>

      <Content>

        {!isDashboardLoading && selectedDashboard && selectedProject && (
          <NavigationPanel
            isExpanded={!isNavigationPanelCollapsed}
            onToggle={() => {
              setIsNavigationPanelCollapsed(isExpanded => !isExpanded);
            }}
            searchBar={
              <div style={{ flex: 1 }}>
                <SearchBar
                  searchTerm={currentSearchTerm}
                  searchResults={currentSearchResults}
                  showResults={currentShowResults}
                  isGlobalSearch={currentIsGlobalSearch}
                  selectedSubcategory={currentSelectedSubcategory}
                  onSearch={handleSearch}
                  onResultSelect={(result) => {
                    const { selectedNode, pathToNode } = handleResultSelect(result);
                    if (activeTab === 'configure') {
                      setTreeExpandedState(pathToNode);
                      handleTreeSelection(selectedNode);
                    } else if (activeTab === 'preview') {
                      // Clear selection first, then select after a short delay
                      // to ensure the radar chart re-renders properly
                      handleRadarSelection(null);
                      setTimeout(() => {
                        handleRadarSelection(selectedNode);
                      }, 300);
                    }
                  }}
                  onToggleSearchMode={() => {
                    if (activeTab === 'configure') {
                      setIsTreeGlobalSearch(!isTreeGlobalSearch);
                    } else {
                      setIsRadarGlobalSearch(!isRadarGlobalSearch);
                    }
                  }}
                />
              </div>
            }
            breadcrumb={
              <Breadcrumb
                data={activeTab === 'configure' ? getFilteredData() : dashboardHierarchyData || {}}
                selectedSubcategory={currentSelectedSubcategory}
                onNavigate={handleChartSelection}
              />
            }
            isVisible={arePanelsVisible}
            onTagSelect={() => setIsTagSelectorOpen(true)}
            selectedTagsCount={selectedTags.length}
            configMode={TREE_CHART_MODES.DASHBOARD}
            showOnlySelected={showOnlySelected}
            onToggleShowSelected={() => setShowOnlySelected(!showOnlySelected)}
            chartType={activeTab === 'configure' ? TREE_CHART_MODES.TREE : TREE_CHART_MODES.RADAR}
            selectedCount={selectedCount}
          />
        )}

        {selectedDashboard && selectedProject && (
          <TabContainer>
            <TabButton
              type="button"
              active={activeTab === 'configure'}
              onClick={() => setActiveTab('configure')}
            >
              Configure
            </TabButton>
            <TabButton
              type="button"
              active={activeTab === 'preview'}
              onClick={() => setActiveTab('preview')}
            >
              Preview
            </TabButton>
          </TabContainer>
        )}

        <ChartPanel>
          {isDashboardLoading ? (
            <LoadingSpinner message="Loading dashboard data..." />
          ) : selectedDashboard && selectedProject?.data ? (
            <>
              {activeTab === 'configure' && (
                <div style={{ position: 'relative', height: '100%' }}>
                  <TreeChart
                    data={filteredData}
                    selectedSubcategory={treeSubcategory}
                    projectData={null}
                    hoveredItem={null}
                    onHover={() => {}}
                    onMouseOut={() => {}}
                    onSelection={handleTreeSelection}
                    onBack={handleTreeBack}
                    zoomFactor={treeZoomFactor}
                    configMode={TREE_CHART_MODES.DASHBOARD}
                    selectedProject={selectedProject}
                    selectedDashboard={selectedDashboard}
                    expandedPath={treeExpandedState}
                    setExpandedPath={setTreeExpandedState}
                    isLeafAssigned={isLeafAssigned}
                    initialCheckedLeafs={initialCheckedLeafs}
                    onLeafToggle={handleLeafToggle}
                    isTreeExpanded={isTreeExpanded}
                    onTreeExpansionToggle={setIsTreeExpanded}
                    onAddInternalNode={handleAddInternalNode}
                    onRemoveInternalNode={handleRemoveInternalNode}
                  />
                  <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '1rem' }}>
                    <ZoomControls
                      zoomFactor={treeZoomFactor}
                      onZoomIn={(isMax) => handleZoomIn(TREE_CHART_MODES.TREE, isMax)}
                      onZoomOut={() => handleZoomOut(TREE_CHART_MODES.TREE)}
                      onReset={() => handleResetPosition(TREE_CHART_MODES.TREE)}
                    />
                  </div>
                </div>
              )}
              
              {activeTab === 'preview' && dashboardHierarchyData && (
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '1rem',
                  height: '100%',
                }}>
                  <div style={{
                    flex: 1,
                    position: 'relative',
                  }}>
                    <div style={{ 
                      position: 'absolute', 
                      top: '10px', 
                      left: '10px', 
                      zIndex: 1,
                      background: 'white',
                      padding: '8px',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <button
                        onClick={() => setViewMode(viewMode === 'traditional' ? 'quadrant' : 'traditional')}
                        style={{
                          padding: '8px 16px',
                          background: COLORS.SECONDARY,
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        Switch to {viewMode === 'traditional' ? 'Quadrant' : 'Traditional'} View
                      </button>
                    </div>
                    
                    {viewMode === 'traditional' ? (
                      <RadarChart
                        data={dashboardHierarchyData}
                        selectedSubcategory={radarSubcategory}
                        zoomFactor={radarZoomFactor}
                        isDragging={isDragging}
                        hoveredItem={hoveredItem}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onHover={handleHover}
                        onMouseOut={handleMouseOut}
                        onSelection={handleRadarSelection}
                        onBack={handleRadarBack}
                      />
                    ) : (
                      <QuadrantRadarChart
                        data={dashboardHierarchyData}
                        hoveredItem={hoveredItem}
                        onHover={handleHover}
                        onMouseOut={handleMouseOut}
                        zoomFactor={radarZoomFactor}
                      />
                    )}
                  </div>

                  {/* Add fixed tooltip panel */}
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem',
                    alignItems: 'flex-start',
                  }}>
                    {viewMode === 'traditional' && (
                      <div style={{
                        width: '300px',
                      }}>
                        <div style={{
                          width: '100%',
                        }}>
                          <Tooltip 
                            data={tooltipData}
                            position={tooltipPosition}
                            approvalStageColors={APPROVAL_STAGE_COLORS}
                            isVisible={!!tooltipData}
                            isFixed={true}
                          />
                        </div>
                      </div>
                    )}

                    <ZoomControls
                      onZoomIn={handleZoomIn}
                      onZoomOut={handleZoomOut}
                      zoomFactor={radarZoomFactor}
                    />
                  </div>
                </div>
              )}
            </>
          ) : null}
        </ChartPanel>
      </Content>

      {/* Add the Modal for TagSelector */}
      <TagSelectorModalComponent
        isOpen={isTagSelectorOpen}
        onClose={() => setIsTagSelectorOpen(false)}
        tags={tags}
        selectedTags={selectedTags}
        onTagToggle={handleTagToggle}
      />
    </Container>
  );
};

export default DashboardConfiguration; 

